import {
  DotsThree,
  MagnifyingGlass,
  Plus,
  TrashSimple,
} from '@phosphor-icons/react'
import { Button, Input, List, Popover } from 'antd'
import VirtualList from 'rc-virtual-list'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { EditableBadge } from '@/components/Badges/EditableBadge'
import { CustomerModel, TagModel } from '@/domain/model/customer'
import { QUERIES } from '@/enums/queries'
import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import { useLoadCustomer } from '@/queries/customers/useLoadCustomer'
import { useLoadTagsClient } from '@/queries/customers/useLoadTagsClient'
import { useUpdateCustomerById } from '@/queries/customers/useUpdateCustomerById'
import { globalQueryClient } from '@/queries/queryClient'
import Tooltip from '@/ui/atoms/Tooltip'

export const AddBadgeButtonClient = () => {
  const { clientId } = useParams<{ clientId: string }>()

  const updateParams = useUpdateURLSearchParams()
  const [search, setSearch] = useState<string | undefined>()
  const [isOpenPopover, setIsOpenPopover] = useState(false)
  const { data: clientDetail } = useLoadCustomer(clientId)
  const { updateCustomerById } = useUpdateCustomerById()

  const clientDetailData = clientDetail as CustomerModel

  const tagListByClient = clientDetailData?.customer_tags || []

  const { data: badgeData = [] } = useLoadTagsClient()

  const filteredBadge = badgeData.filter(
    (item) =>
      item.name.toLowerCase().includes(search?.toLocaleLowerCase() || '') &&
      tagListByClient.every((tag) => tag.id !== item.id)
  )

  const addTagIntoClient = async (id?: string) => {
    const getTagById = badgeData.find((item) => item.id === id) as TagModel
    if (!getTagById) {
      toast.error('Error trying to add a tag')
      return
    }
    try {
      globalQueryClient.setQueryData<CustomerModel>(
        [QUERIES.CUSTOMERS.GET, clientId],
        (old) => {
          if (!old) return old
          return {
            ...old,
            customer_tags: [...(old.customer_tags || []), getTagById],
          }
        }
      )
      await updateCustomerById({
        id: clientId,
        customer_tags: [
          ...(clientDetailData?.customer_tags || []),
          { ...getTagById, id: undefined },
        ],
      })
    } catch (error) {
      globalQueryClient.setQueryData<CustomerModel>(
        [QUERIES.CUSTOMERS.GET, clientId],
        (old) => {
          if (!old) return old
          return {
            ...old,
            customer_tags: (old.customer_tags || []).filter(
              (item) => item.id !== getTagById.id
            ),
          }
        }
      )
      toast.error('Error trying to add a tag')
    }
  }
  return (
    <Popover
      classNames={{ body: 'p-0 min-w-[200px]' }}
      content={
        <div className="flex flex-col">
          <div className="flex items-center justify-center border-b border-b-gray-200/80 px-2 py-2">
            <Input
              className="h-8 w-full"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search..."
              prefix={<MagnifyingGlass className="text-gray-600" />}
            />
            <Tooltip mouseEnterDelay={0.2} title="Add new tag">
              <Button
                className="h-8 py-0 pl-2 pr-1 !outline-none"
                onClick={() => {
                  setIsOpenPopover(false)
                  updateParams(['createTag', 'open'])
                }}
                type="link"
              >
                <Plus weight="bold" />
              </Button>
            </Tooltip>
          </div>
          {!badgeData.length ? (
            <span className="w-full py-3 text-center text-gray-500">
              No tags created
            </span>
          ) : null}
          {!filteredBadge.length && badgeData.length ? (
            <span className="w-full py-3 text-center text-gray-500">
              No tags found
            </span>
          ) : badgeData.length ? (
            <List>
              <VirtualList
                data={filteredBadge}
                height={270}
                itemHeight={37}
                itemKey="id"
                style={{
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {({ id, name, color }: TagModel) => (
                  <List.Item className="p-0" key={id}>
                    <div className="flex w-full">
                      <button
                        className="relative flex w-full items-center gap-1 px-2 py-2 font-medium !outline-none hover:bg-gray-100/70 [&:hover_.badge-details-dots]:flex"
                        key={id}
                        onClick={() => addTagIntoClient(id)}
                      >
                        <EditableBadge.RawItem
                          className="cursor-pointer"
                          {...{ color }}
                        >
                          {name}
                        </EditableBadge.RawItem>
                        <div className="badge-details-dots absolute right-3 hidden h-full items-center justify-start gap-1 pl-1">
                          <div
                            className="flex h-full items-center justify-start text-gray-400/70 !outline-none hover:text-gray-700"
                            onClick={(event) => {
                              event.stopPropagation()
                              setIsOpenPopover(false)
                              updateParams(['editTag', id])
                            }}
                          >
                            <DotsThree className="text-xl" weight="bold" />
                          </div>
                          <div
                            className="flex h-full items-center justify-start text-gray-400/70 !outline-none hover:text-red-700"
                            onClick={(event) => {
                              event.stopPropagation()
                              setIsOpenPopover(false)
                              updateParams(['deleteTag', id])
                            }}
                          >
                            <TrashSimple className="text-base" weight="bold" />
                          </div>
                        </div>
                      </button>
                    </div>
                  </List.Item>
                )}
              </VirtualList>
            </List>
          ) : null}
        </div>
      }
      onOpenChange={(value) => setIsOpenPopover(value)}
      open={isOpenPopover}
      placement="bottomLeft"
      trigger="click"
    >
      <EditableBadge.AddButton />
    </Popover>
  )
}
